import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Phone, CheckCircle2, XCircle, Wifi, WifiOff, Plus, Loader2, Key, Copy, RefreshCw, Zap } from 'lucide-react';
import api, { getWhatsAppNumbers, updateWhatsAppNumber } from '../services/api';

const META_APP_ID = '957751667117405';

function BusinessAccounts() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [bmInfoMap, setBmInfoMap] = useState({}); // { numberId: { name, id } }
  const [loading, setLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  
  // Estados para o Modal de Verificação OTP
  const [verifyingNumber, setVerifyingNumber] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [syncingId, setSyncingId] = useState(null); // ⭐ NOVO: sincronizar nome manualmente

  useEffect(() => {
    if (!window.FB) {
      window.fbAsyncInit = function() {
        window.FB.init({
          appId      : META_APP_ID,
          cookie     : true,
          xfbml      : true,
          version    : 'v19.0'
        });
      };
      (function(d, s, id) {
        var js, fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) return;
        js = d.createElement(s); js.id = id;
        js.src = "https://connect.facebook.net/en_US/sdk.js";
        fjs.parentNode.insertBefore(js, fjs );
      }(document, 'script', 'facebook-jssdk'));
    }

    const token = localStorage.getItem('token');
    if (token) {
      loadData();
    } else {
      setLoading(false);
    }
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [businessesRes, numbersRes] = await Promise.all([
        api.get('/api/businesses'),
        getWhatsAppNumbers()
      ]);
      setBusinesses(businessesRes.data);
      setWhatsappNumbers(numbersRes.data);
      setLoading(false);

      // ⭐ CORRIGIDO: essa rota (/api/whatsapp-numbers) não devolve waba_id no
      // número — precisa cruzar com a lista de businesses (que já tem waba_id)
      // pelo business_id, pra saber se dá pra consultar
      numbersRes.data.forEach(n => {
        const parentBusiness = businessesRes.data.find(b => b.id === n.business_id);
        if (parentBusiness?.waba_id) {
          api.get(`/api/phone-numbers/${n.id}/meta-status`)
            .then(res => {
              if (res.data?.business_manager_name) {
                setBmInfoMap(prev => ({ ...prev, [n.id]: { name: res.data.business_manager_name, id: res.data.business_manager_id } }));
              }
            })
            .catch(() => {});
        }
      });
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const launchWhatsAppSignup = () => {
    if (isConnecting) return;
    if (!window.FB) {
      alert('SDK do Facebook ainda está carregando. Tente novamente em alguns segundos.');
      return;
    }
    setIsConnecting(true);
    window.FB.login((response) => {
      setIsConnecting(false);
      if (response.authResponse) {
        const code = response.authResponse.code;
        handleExchangeCode(code);
      } else {
        alert('Usuário cancelou o fluxo de conexão.');
      }
    }, {
      config_id: '2052678948656395',
      response_type: 'code',
      override_default_response_type: true,
      scope: 'whatsapp_business_management,whatsapp_business_messaging',
      extras: { feature: 'whatsapp_embedded_signup', sessionInfoVersion: '3' }
    });
  };

  const handleExchangeCode = async (code) => {
    try {
      const res = await api.post('/api/auth/whatsapp-exchange', { code });
      if (res.data.success) {
        alert('Cliente conectado com sucesso!');
        loadData();
      }
    } catch (err) {
      alert('Erro na conexão: ' + (err.response?.data?.error || err.message));
    }
  };

  // ⭐ NOVO: busca o nome atualizado direto na Meta (o Business Manager não avisa
  // essa mudança por webhook) e atualiza só essa conta na tela, sem recarregar tudo
  const handleSync = async (businessId) => {
    setSyncingId(businessId);
    try {
      const res = await api.post(`/api/businesses/${businessId}/sync`);
      setBusinesses(prev => prev.map(b =>
        b.id === businessId ? { ...b, profile_name: res.data.profile_name } : b
      ));
    } catch (err) {
      alert('Erro ao sincronizar: ' + (err.response?.data?.error || err.message));
    } finally {
      setSyncingId(null);
    }
  };

  const handleDisconnect = async (id) => {
    // ⭐ NOVO: lembra de conferir o saldo pendente na Meta antes de desconectar,
    // pra evitar surpresa de cobrança presa (o Souli desconecta só do nosso lado,
    // não mexe no saldo/faturamento que continua sendo direto com a Meta)
    if (window.confirm('Deseja realmente desconectar esta conta?\n\n⚠️ Antes de continuar, confira se não tem saldo pendente no Meta Business Suite (Cobrança e Pagamentos). Desconectar aqui não quita nenhum valor devido à Meta — isso é resolvido direto lá.')) {
      try {
        await api.delete(`/api/businesses/${id}`);
        loadData();
      } catch (err) {
        alert('Erro ao desconectar: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const toggleNumberStatus = async (numberId, currentStatus) => {
    try {
      await updateWhatsAppNumber(numberId, { is_active: !currentStatus });
      loadData();
    } catch (err) {
      alert('Erro ao atualizar status do número');
    }
  };

  // Funções de Verificação OTP
  const handleRequestCode = async (method = 'SMS') => {
    if (!verifyingNumber) return;
    setIsRequestingCode(true);
    try {
      await api.post('/api/auth/whatsapp/request-code', {
        phoneNumberId: verifyingNumber.phone_number_id,
        wabaId: verifyingNumber.waba_id,
        method
      });
      alert('Código enviado! Verifique seu telefone.');
    } catch (err) {
      alert('Erro ao solicitar código: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsRequestingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verifyingNumber || !verificationCode) return;
    setIsRegistering(true);
    try {
      await api.post('/api/auth/whatsapp/register', {
        phoneNumberId: verifyingNumber.phone_number_id,
        wabaId: verifyingNumber.waba_id,
        code: verificationCode
      });
      alert('Número verificado e ativado com sucesso!');
      setVerifyingNumber(null);
      setVerificationCode('');
      loadData();
    } catch (err) {
      alert('Erro na verificação: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contas Business</h1>
          <p className="text-gray-500">Gerencie as contas WhatsApp Business conectadas</p>
        </div>
        <div className="flex items-center gap-3">
          {/* ⭐ NOVO: leva pro fluxo de Embedded Signup que já suporta Coexistência
              (número existente no WhatsApp Business App + API ao mesmo tempo) */}
          <button
            onClick={() => navigate('/embedded-signup')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-all"
          >
            <Zap size={18} className="text-amber-500" />
            Conectar com Coexistência
          </button>
          <button 
            onClick={launchWhatsAppSignup}
            disabled={isConnecting}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 shadow-lg shadow-green-100 transition-all disabled:opacity-50"
          >
            {isConnecting ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
            {isConnecting ? 'Conectar Nova Conta' : 'Conectar Nova Conta'}
          </button>
        </div>
      </div>

      <div className="grid gap-6">
        {businesses.length > 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="space-y-6">
              {businesses.map((business) => (
                <div key={business.id}>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">NÚMEROS VINCULADOS</h4>
                  <div className="space-y-3">
                    {whatsappNumbers.filter(n => n.business_id === business.id).map((number) => (
                      <div key={number.id} className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-100 hover:border-green-200 transition-colors flex-wrap">
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center text-green-600 flex-shrink-0">
                            <CheckCircle2 size={20} />
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 text-sm">{business.profile_name || 'Conta WhatsApp'}</p>
                            <p className="text-xs text-gray-500">ID: {business.waba_id}</p>
                            {bmInfoMap[number.id]?.name && (
                              <p className="text-xs text-purple-600 font-medium mt-0.5">
                                BM: {bmInfoMap[number.id].name}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => handleSync(business.id)}
                            disabled={syncingId === business.id}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors ml-1 disabled:opacity-50"
                            title="Sincronizar nome com a Meta"
                          >
                            {syncingId === business.id
                              ? <Loader2 size={18} className="animate-spin" />
                              : <RefreshCw size={18} />}
                          </button>
                          <button
                            onClick={() => handleDisconnect(business.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Desconectar conta"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>

                        <div className="flex items-center gap-3 flex-1 min-w-[220px]">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${number.is_active ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
                            <Phone size={18} />
                          </div>
                          <div className="min-w-0">
                            {/* EXIBIÇÃO DO NÚMERO CORRIGIDA — lê display_phone_number, que é o campo real preenchido pela Meta */}
                            <p className="font-medium text-gray-900 text-sm">
                              {number.display_phone_number || number.phone_number || 'Número não identificado'}
                            </p>
                            <p className="text-xs text-gray-500">{number.display_name || number.display_phone_number || number.phone_number}</p>
                            <div className="mt-1 space-y-0.5">
                              <p className="text-[10px] text-gray-400">
                                Phone Number ID: {number.phone_number_id}
                                <button onClick={() => navigator.clipboard.writeText(number.phone_number_id)} className="text-blue-400 hover:underline ml-1">copiar</button>
                              </p>
                              <p className="text-[10px] text-gray-400">WABA ID: {business.waba_id}</p>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 flex-shrink-0">
                          {/* BOTÃO VERIFICAR APARECE SE NÃO ESTIVER ATIVO */}
                          {!number.is_active && (
                            <button
                              onClick={() => setVerifyingNumber({...number, waba_id: business.waba_id})}
                              className="flex items-center gap-2 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors shadow-sm"
                            >
                              <Key size={14} />
                              Verificar
                            </button>
                          )}
                          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                              number.is_active ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                            }`}>
                            {number.is_active ? <Wifi size={16} /> : <WifiOff size={16} />}
                            {number.is_active ? 'Ativo' : 'Pendente'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-gray-100">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
              <WifiOff size={40} />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">Nenhuma conta conectada</h3>
            <p className="text-gray-500 mb-8">Conecte sua primeira conta WhatsApp Business para começar</p>
            <button 
              onClick={launchWhatsAppSignup}
              disabled={isConnecting}
              className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
            >
              {isConnecting ? <Loader2 size={18} className="animate-spin" /> : null} 
              {isConnecting ? 'Conectando...' : 'Conectar Conta'}
            </button>
          </div>
        )}
      </div>

      {/* Modal de Verificação OTP */}
      {verifyingNumber && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Key size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Verificar Número</h2>
              <p className="text-gray-500 mt-2">
                Enviaremos um código para {verifyingNumber.display_phone_number || verifyingNumber.phone_number}
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex gap-2">
                <button 
                  onClick={() => handleRequestCode('SMS')}
                  disabled={isRequestingCode}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 disabled:opacity-50"
                >
                  {isRequestingCode ? 'Enviando...' : 'Receber SMS'}
                </button>
                <button 
                  onClick={() => handleRequestCode('VOICE')}
                  disabled={isRequestingCode}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 disabled:opacity-50"
                >
                  Receber Ligação
                </button>
              </div>

              <div className="relative">
                <input 
                  type="text" 
                  maxLength="6"
                  placeholder="Código de 6 dígitos"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl text-center text-2xl font-bold tracking-[0.2em] focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>

              <button 
                onClick={handleVerifyCode}
                disabled={isRegistering || verificationCode.length < 6}
                className="w-full py-4 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 shadow-lg disabled:opacity-50 transition-all"
              >
                {isRegistering ? 'Verificando...' : 'Confirmar e Ativar'}
              </button>

              <button 
                onClick={() => setVerifyingNumber(null)}
                className="w-full py-2 text-gray-400 text-sm hover:text-gray-600"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BusinessAccounts;
